import {
  Component,
  ContentChild,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { FloatLabelType } from '@angular/material/form-field';
import { CustomValidators } from '@smartcharge/shared/index';
import { debounceTime } from 'rxjs';
import { CustomWS, WebsocketService } from 'src/app/services/websocket/websocket.service';
import { UserType } from 'src/app/user-roles';
import { ToastService } from '../../../shared/services/toast/toast.service';

type DisplayFunction = (item: any) => string;

@Component({
  selector: 'sc-socket-search',
  templateUrl: './socket-search.component.html',
  styleUrls: ['./socket-search.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class SocketSearchComponent implements OnInit, OnDestroy, OnChanges {
  @ViewChild('autoComplete', { read: MatAutocompleteTrigger })
  auto: MatAutocompleteTrigger;

  @Output() search: EventEmitter<any> = new EventEmitter();
  @Output() itemSelected: EventEmitter<any> = new EventEmitter();
  @Output() clear: EventEmitter<any> = new EventEmitter();
  @Output() results: EventEmitter<any> = new EventEmitter();

  @Input() displayFn: DisplayFunction;
  @Input() socketURL: string;
  @Input() errorMessage: string;
  @Input() placeholder: string;
  @Input() dropdownLimit: number = 4;
  @Input() matFormFieldClass?: string = '';
  @Input() selectedItem: any;
  @Input() searchInput: string;
  @Input() acceptCustomValue: boolean = false;
  @Input() hint: string = '';

  @Input() inputRequired: boolean = false;
  @Input() floatingLabel: FloatLabelType = 'auto';

  searchInputCtrl: FormControl = new FormControl('');
  searchSocket: CustomWS;
  socketRetries: number = 0;
  searchInitiated: boolean = false;
  userType = UserType;
  searchItems: any[] = [];

  @ContentChild(TemplateRef) templateRef: TemplateRef<any>;

  constructor(
    private ws: WebsocketService,
    private toastService: ToastService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.selectedItem?.currentValue) {
      this.searchInputCtrl.patchValue(this.displayFn(changes?.selectedItem?.currentValue));
    }
    if (changes.searchInput) {
      this.searchInputCtrl.setValue(this.searchInput);
    }
    if (changes?.acceptCustomValue?.currentValue) {
      this.searchInputCtrl.setValidators([Validators.required, this.validateCustomValue.bind(this)]);
    }
  }

  ngOnInit(): void {
    this.initSocket();

    this.searchInputCtrl.valueChanges.pipe(debounceTime(200)).subscribe((value) => {
      if (typeof value === 'string') {
        let search = {
          Text: value ? value.trim() : '',
          Nb: this.dropdownLimit
        };

        if (this.searchInitiated && this.searchSocket) {
          this.searchSocket.webSocket.next(search);
        }
      }
    });
  }

  onBlur() {
    if (this.acceptCustomValue && this.searchInputCtrl.value && !this.searchInputCtrl.errors) {
      this.search.emit(this.searchInputCtrl.value);
    }
  }

  async initSocket() {
    if (this.searchSocket) return;
    this.searchSocket = await this.ws.init(this.socketURL); //Init companies ws

    this.searchSocket.webSocket.subscribe({
      next: (res) => {
        if (res instanceof Array) {
          this.searchItems = res;
          this.results.emit(this.searchItems);
        }
      },
      error: (err: any) => {
        if (this.socketRetries < 3) {
          this.socketRetries++;
          this.initSocket();
        } else {
          this.socketRetries = 0;
          this.toastService.error(err);
        }
      }
    });

    this.searchSocket.onOpen.then((open) => {
      this.searchInitiated = true;
      this.onSearch(null);
    });
  }

  onSearch(e) {
    if (e && e.key === 'Enter') {
      this.auto.closePanel();

      if (this.searchItems.length === 1 && this.searchItems[0].AccessCard) {
        this.itemSelected.emit(this.searchItems[0]);
      } else {
        this.search.emit(this.searchInputCtrl.value);
      }
    }
  }

  selectItem(e) {
    if (typeof e.option.value === 'string') {
      this.onSearch({ key: 'Enter' });
    } else {
      let item: any = e.option.value;
      this.itemSelected.emit(item);
    }
  }

  clearSocket() {
    if (this.searchSocket) {
      this.searchInitiated = false;
      this.searchSocket.webSocket.complete();
      this.searchSocket = null;
    }
  }

  clearSearch(event: MouseEvent) {
    event.stopPropagation();

    this.searchInputCtrl.patchValue('');
    this.auto.closePanel();
    this.itemSelected.emit(null);
    this.clear.emit(null);

    this.searchItems = [];
  }

  ngOnDestroy() {
    this.clearSocket();
  }

  private validateCustomValue(control: FormControl) {
    const value = control.value;

    if (typeof value === 'object' && 'PK_CustomerID' in value && value.PK_CustomerID) {
      return null;
    } else if (typeof value === 'string') {
      return CustomValidators.email(control);
    }

    return { invalidCustomValue: true }; // invalid
  }
}
